import { captureException } from '~/errorsLogging';

/**
 * LocalStorageService
 *
 * A type-safe static class to interact with browser's localStorage.
 */
export class LocalStorageService {
  /**
   * Stores a value in localStorage under the specified key.
   * The value is serialized to JSON before storage.
   *
   * @param key - The key under which the value is stored.
   * @param value - The value to store. Must be JSON-serializable.
   */
  public static setItem<T>(key: string, value: T): void {
    try {
      const serializedValue = JSON.stringify(value);
      window?.localStorage.setItem(key, serializedValue);
    } catch (error) {
      captureException(
        new Error(
          `Error setting item in localStorage. Key: ${key}, Value: ${value}`,
        ),
      );
    }
  }

  /**
   * Retrieves a value from localStorage by key and parses it as the specified type.
   *
   * @param key - The key of the item to retrieve.
   * @returns The parsed value if found and parsed successfully; otherwise, null.
   */
  public static getItem<T>(key: string): T | null {
    try {
      const serializedValue = window?.localStorage.getItem(key);
      if (serializedValue === null) {
        return null;
      }
      return JSON.parse(serializedValue) as T;
    } catch {
      captureException(
        new Error(`Error getting item from localStorage. Key: ${key}`),
      );
      return null;
    }
  }

  /**
   * Removes an item from localStorage by key.
   *
   * @param key - The key of the item to remove.
   */
  public static removeItem(key: string): void {
    try {
      window?.localStorage.removeItem(key);
    } catch {
      captureException(
        new Error(`Error removing item from localStorage. Key: ${key}`),
      );
    }
  }

  /**
   * Clears all items from localStorage.
   */
  public static clear(): void {
    try {
      window?.localStorage.clear();
    } catch {
      captureException(new Error('Error clearing localStorage.'));
    }
  }

  /**
   * Checks if a key exists in localStorage.
   *
   * @param key - The key to check for existence.
   * @returns True if the key exists; otherwise, false.
   */
  public static hasKey(key: string): boolean {
    try {
      return window?.localStorage.getItem(key) !== null;
    } catch {
      captureException(new Error('Error checking key in localStorage.'));
      return false;
    }
  }

  /**
   * Retrieves all keys stored in localStorage.
   *
   * @returns An array of all keys in localStorage.
   */
  public static getAllKeys(): string[] {
    const keys: string[] = [];
    try {
      for (let i = 0; i < window?.localStorage.length; i++) {
        const key = window?.localStorage.key(i);
        if (key !== null) {
          keys.push(key);
        }
      }
    } catch {
      captureException(new Error('Error retrieving keys in localStorage.'));
    }
    return keys;
  }
}
